// Brand-aware reorder URL resolver. Called from Today's Plan + the
// EquipmentSection PartsList when a part's user-set reorder_url is
// empty — produces a sensible default by combining:
//
//   1. the part_kind + brand → search query string from the library
//   2. the user's preferred retailer's search URL template
//
// Falling back gracefully:
//   - brand-specific query → kind-level _default_query
//   - preferred retailer → first available retailer in the list
//   - empty library → null (caller hides the "Reorder →" button)
//
// Resolver order:
//   explicit URL on the part > library lookup > nothing
import { safeReorderHref } from '../../shared/reorder-link.js';

export function resolveReorderUrl({ reorderUrl, partKind, brand, library, preferredRetailer }) {
  // User-set URL on the part always wins. That's what the explicit
  // reorder_url field is for — overrides per row. A stored link without
  // https:// would open inside TankSensei; safeReorderHref repairs it.
  const own = safeReorderHref(reorderUrl);
  if (own) return own;
  if (!library || !partKind) return null;
  const links = library.links || {};
  const entry = links[partKind];
  if (!entry) return null;
  // Pick the search query — brand-specific wins, else the kind's
  // _default_query, else nothing.
  let q = null;
  if (brand && entry[brand] && entry[brand].q) {
    q = entry[brand].q;
  } else if (entry._default_query) {
    q = entry._default_query;
  }
  if (!q) return null;
  // Pick the retailer template. The user's preferred wins, else the
  // first retailer in the list (BRS in the shipped seed).
  const retailers = Array.isArray(library.retailers) ? library.retailers : [];
  if (!retailers.length) return null;
  const retailer =
    (preferredRetailer && retailers.find((r) => r.k === preferredRetailer)) || retailers[0];
  if (!retailer?.search_url) return null;
  return retailer.search_url.replace('{q}', encodeURIComponent(q));
}

// Supplies variant: the search query IS the product name the keeper
// already gave the supply ("BRS Soda Ash 5 lb"), so every supply gets
// a working Reorder link with zero setup. A pasted reorder_url still
// wins as the per-row override.
export function resolveSupplyReorderUrl({ reorderUrl, name, library, preferredRetailer }) {
  const own = safeReorderHref(reorderUrl);
  if (own) return own;
  const q = String(name || '').trim();
  if (!q) return null;
  const retailers = Array.isArray(library?.retailers) ? library.retailers : [];
  if (!retailers.length) return null;
  const retailer =
    (preferredRetailer && retailers.find((r) => r.k === preferredRetailer)) || retailers[0];
  if (!retailer?.search_url) return null;
  return retailer.search_url.replace('{q}', encodeURIComponent(q));
}

// Convenience: returns just the retailer being used to resolve. Lets
// the UI render "via BRS" / "via Marine Depot" alongside the link so
// the user knows where the click is going before they tap.
export function resolveRetailerForUser({ library, preferredRetailer }) {
  const retailers = Array.isArray(library?.retailers) ? library.retailers : [];
  if (!retailers.length) return null;
  return (
    (preferredRetailer && retailers.find((r) => r.k === preferredRetailer)) || retailers[0] || null
  );
}
